<script setup>
import { reactive, ref, onMounted, watch, nextTick, computed } from 'vue';
import { useStore } from 'vuex';
import api from '@/services/api.js';
import ArgonInput from '@/components/ArgonInput.vue';
import logo from "@/assets/img/fondotam-blanco.png";
import Swal from "sweetalert2";
import { generarEtiquetaTermica } from '@/js/ImpresionEtiqueta.js';
import SelectorJerarquicoPersonal from '@/components/SelectorJerarquicoPersonal.vue';
import PreviewEtiquetaActivo from '@/components/PreviewEtiquetaActivo.vue';
import { validarRequeridos, validarAlMenosUno } from '@/utils/validators.js';
import { construirUrlImagen } from '@/utils/formatters.js';

// --- ESTADOS Y FORMULARIOS ---
const store = useStore();
const cargando = ref(false);
const qrUrl = ref(null);
const mostrarModalExtra = ref(false);
const tipoProcesoExtra = ref('');
const idActivoRecienCreado = ref(null);
const procesoFinalizadoExitosa = ref(false);
const esGrupoIA = ref(false);

const busquedaTipo = ref('');
const inputFoco = ref(false);
const todosLosTipos = ref([]);

const tiposSugeridos = computed(() => {
  if (!busquedaTipo.value) return [];
  const text = busquedaTipo.value.toLowerCase();
  return todosLosTipos.value.filter(t => t.nombre.toLowerCase().includes(text));
});

const seleccionarTipo = (tipo) => {
  formularioActivo.tipo_activo = tipo.id;
  formularioActivo.grupo_contable_seleccionado = tipo.grupo_contable;
  busquedaTipo.value = tipo.nombre;
  inputFoco.value = false;
};

const ocultarFoco = () => {
  setTimeout(() => {
    inputFoco.value = false;
  }, 200);
};

const previsualizaciones = reactive({
  imagen_activo: null,
  imagen_lateral: null,
  imagen_sn: null
});

const datosEtiqueta = reactive({
  codigo_tamep: '',
  codigo_tab: '',
  serie: ''
});

const formularioActivo = reactive({
  codigo_tamep: '',
  codigo_tab: '',
  nombre_especifico: '',
  serie: '',
  descripcion: '',
  especificaciones: '',
  fecha_adquisicion: new Date().toISOString().substr(0, 10),
  costo: 0,
  grupo_contable_seleccionado: null,
  tipo_activo: null,
  regional: null,
  gerencia: null,
  seccion: null,
  ubicacion_base: null,
  estado_actual: 'Bueno',
  situacion_activo: 'Disponible',
  perfil_uso: 'N/A',
  uso_inicial_estimado: 0,
  contador_fallas: 0,
  imagen_activo: null,
  imagen_lateral: null,
  imagen_sn: null
});

const techSpecs = reactive({
  cpu: '', ram: '', disco: '', mac: '', so: '', voltaje: '220V'
});

const gruposContables = computed(() => store.state.catalogo.gruposContables);
const regionalesList = ref([]);
const gerenciasList = ref([]);
const seccionesList = ref([]);

const formularioExtra = reactive({
  regional: '', gerencia: '', seccion: '', cargo: '', usuario: '',
  ubicacion: '', motivo_cambio_o_baja: '', tipo: 'Permanente',
  num_acta: '', documento_respaldo: null, fecha_fin_estimada: '', observaciones: ''
});

// --- CARGA DE DATOS INICIAL ---
onMounted(async () => {
  try {
    cargando.value = true;
    await store.dispatch('catalogo/fetchParametrosIniciales');
    const [resTipos, resReg] = await Promise.all([
      api.get('tipos-activos/'),
      api.get('regionales/')
    ]);
    todosLosTipos.value = resTipos.data.results || resTipos.data;
    regionalesList.value = resReg.data.results || resReg.data;
  } catch (e) {
    console.error("Error de carga:", e);
  } finally { cargando.value = false; }
});

watch(() => formularioActivo.regional, async (newVal) => {
  formularioActivo.gerencia = null;
  formularioActivo.seccion = null;
  gerenciasList.value = [];
  seccionesList.value = [];
  if (newVal) {
    try {
      const { data } = await api.get(`gerencias/?regional=${newVal}`);
      gerenciasList.value = data.results || data;
    } catch (e) { console.error(e); }
  }
});

watch(() => formularioActivo.gerencia, async (newVal) => {
  formularioActivo.seccion = null;
  seccionesList.value = [];
  if (newVal) {
    try {
      const { data } = await api.get(`secciones/?gerencia=${newVal}`);
      seccionesList.value = data.results || data;
    } catch (e) { console.error(e); }
  }
});

// --- WATCHERS DINÁMICOS ---
watch(() => formularioActivo.grupo_contable_seleccionado, async (id) => {
  if (id) {
    const grupo = gruposContables.value.find(g => g.id === id);
    esGrupoIA.value = grupo ? grupo.tiene_mantenimiento_predictivo : false;

    if (!esGrupoIA.value) {
      formularioActivo.perfil_uso = 'N/A';
      formularioActivo.uso_inicial_estimado = 0;
    } else if (formularioActivo.perfil_uso === 'N/A') {
      formularioActivo.perfil_uso = 'Oficina'; 
    }
  } else {
    esGrupoIA.value = false;
  }
});



watch(() => [formularioActivo.codigo_tamep, formularioActivo.codigo_tab, formularioActivo.nombre_especifico], (nuevos) => {
  if (qrUrl.value && (nuevos[0] !== '' || nuevos[1] !== '' || nuevos[2] !== '')) {
    qrUrl.value = null;
    procesoFinalizadoExitosa.value = false;
  }
});

const limpiarFormulario = () => {
  Object.assign(formularioActivo, {
    codigo_tamep: '', codigo_tab: '', nombre_especifico: '', serie: '', descripcion: '',
    especificaciones: '', fecha_adquisicion: new Date().toISOString().substr(0, 10), costo: 0,
    grupo_contable_seleccionado: null, tipo_activo: null, regional: null, gerencia: null, seccion: null, ubicacion_base: null,
    estado_actual: 'Bueno', situacion_activo: 'Disponible', perfil_uso: 'N/A',
    uso_inicial_estimado: 0, contador_fallas: 0, imagen_activo: null, imagen_lateral: null, imagen_sn: null
  });
  Object.assign(techSpecs, { cpu: '', ram: '', disco: '', mac: '', so: '', voltaje: '220V' });
  busquedaTipo.value = '';
  inputFoco.value = false;
  Object.assign(previsualizaciones, { imagen_activo: null, imagen_lateral: null, imagen_sn: null });
  const fileInputs = document.querySelectorAll('.file-hidden');
  fileInputs.forEach(input => input.value = '');
  formularioExtra.regional = ''; formularioExtra.gerencia = ''; formularioExtra.seccion = ''; formularioExtra.cargo = ''; formularioExtra.usuario = '';
  formularioExtra.ubicacion = ''; formularioExtra.motivo_cambio_o_baja = ''; formularioExtra.tipo = 'Permanente';
  formularioExtra.num_acta = ''; formularioExtra.documento_respaldo = null; formularioExtra.fecha_fin_estimada = ''; formularioExtra.observaciones = '';
};

// --- FUNCIONES MULTIMEDIA ---
const handleFileUpload = (e, campo) => { 
  const file = e.target.files[0];
  if (!file) return;
  formularioActivo[campo] = file; 
  previsualizaciones[campo] = URL.createObjectURL(file);
};

const eliminarImagen = (campo) => {
  formularioActivo[campo] = null;
  previsualizaciones[campo] = null;
};



const imprimirEtiquetaPDF = () => {
  generarEtiquetaTermica({
    qrUrl: qrUrl.value,
    codigo_tamep: datosEtiqueta.codigo_tamep,
    codigo_tab: datosEtiqueta.codigo_tab,
    serie: datosEtiqueta.serie
  });
};

// --- PROCESOS DE ASIGNACIÓN Y REGISTRO ---
const irAPasoAsignacion = (id) => {
  idActivoRecienCreado.value = id;
  tipoProcesoExtra.value = 'Asignado';
  formularioExtra.tipo = 'Permanente';
  formularioExtra.ubicacion = formularioActivo.ubicacion_base;
  mostrarModalExtra.value = true;
};

const finalizarProcesoCompleto = async () => {
  const camposRequeridos = {
    regional: formularioExtra.regional,
    usuario: formularioExtra.usuario,
    num_acta: formularioExtra.num_acta
  };
  if (!validarRequeridos(camposRequeridos, "Regional, Responsable y Nro. de Acta son obligatorios.")) return;
  cargando.value = true;
  try {
    const fdExtra = new FormData();
    fdExtra.append('activo', idActivoRecienCreado.value);
    fdExtra.append('tipo', formularioExtra.tipo);
    fdExtra.append('regional', formularioExtra.regional);
    fdExtra.append('gerencia', formularioExtra.gerencia);
    fdExtra.append('seccion', formularioExtra.seccion);
    fdExtra.append('cargo', formularioExtra.cargo);
    fdExtra.append('usuario', formularioExtra.usuario);
    fdExtra.append('num_acta', formularioExtra.num_acta);
    fdExtra.append('observaciones', formularioExtra.observaciones);
    fdExtra.append('esta_activa', 'true');
    
    // Ubicación: Si no se eligió una en el modal, usar la ubicación base del activo
    const ubiFinal = formularioExtra.ubicacion || formularioActivo.ubicacion_base;
    fdExtra.append('ubicacion', ubiFinal);

    if (formularioExtra.tipo === 'Temporal' && formularioExtra.fecha_fin_estimada) {
      fdExtra.append('fecha_fin_estimada', formularioExtra.fecha_fin_estimada);
    }

    if (formularioExtra.documento_respaldo) {
      fdExtra.append('documento_respaldo', formularioExtra.documento_respaldo);
    }

    await api.post('designaciones/', fdExtra);
    procesoFinalizadoExitosa.value = true;
    mostrarModalExtra.value = false;
    Swal.fire("Éxito", "Asignación completada correctamente.", "success");
    
    setTimeout(() => { 
      imprimirEtiquetaPDF(); 
      limpiarFormulario();
    }, 600);
  } catch (error) {
    console.error("Error en asignación:", error);
  } finally { cargando.value = false; }
};

const registrarActivo = async () => {
  if (!validarAlMenosUno(
    [formularioActivo.codigo_tamep, formularioActivo.codigo_tab],
    "Debe ingresar obligatoriamente al menos un código (TAMEP o TAB histórico)."
  )) return;
  cargando.value = true;
  const fd = new FormData();
  
  if (esGrupoIA.value) {
    formularioActivo.especificaciones = `CPU: ${techSpecs.cpu} | RAM: ${techSpecs.ram} | Disco: ${techSpecs.disco} | MAC: ${techSpecs.mac} | Voltaje: ${techSpecs.voltaje}`;
  } else {
    formularioActivo.especificaciones = 'N/A (Activo no tecnológico)';
  }

  Object.keys(formularioActivo).forEach(key => {
  let valor = formularioActivo[key];
  
  if (key === 'nombre_especifico' && typeof valor === 'string') {
    valor = valor.toUpperCase().trim();
  }

  // CORRECCIÓN: Si el usuario eligió "Asignar de Inmediato", el activo debe crearse como 'Disponible' 
  // en la BD primero, para que el posterior POST de designaciones no sea rechazado.
  if (key === 'situacion_activo' && valor === 'Asignado') {
    valor = 'Disponible';
  }

  // Ignoramos nulls y strings vacíos
  if (valor !== null && valor !== '') {
    fd.append(key, valor);
  }
});

  try {
    const response = await api.post('activos/', fd);
    if (response.data.id) {
      qrUrl.value = construirUrlImagen(response.data.imagen_qr);
      datosEtiqueta.codigo_tamep = formularioActivo.codigo_tamep;
      datosEtiqueta.codigo_tab = formularioActivo.codigo_tab;
      datosEtiqueta.serie = formularioActivo.serie;

      if (formularioActivo.situacion_activo === 'Disponible') {
        Swal.fire("Éxito", "Activo Registrado en Almacén.", "success");
        
        // Llamada actualizada a jsPDF
      await nextTick(); // Espera a que el v-if dibuje la imagen
      imprimirEtiquetaPDF();
      limpiarFormulario();
      } else {
        irAPasoAsignacion(response.data.id);
      }
    }
  } catch (error) {
    console.error("Error registrando activo:", error);
  } finally { cargando.value = false; }
};



const prepararNuevoRegistro = () => { 
  qrUrl.value = null;
  procesoFinalizadoExitosa.value = false;
  window.scrollTo({ top: 0, behavior: 'smooth' });
};
</script>
<template>
  <div class="container-fluid py-4 min-vh-100 app-transition">
    <form @submit.prevent="registrarActivo">
      <div class="row">
        <div class="col-lg-8">
          <div class="card border-0 shadow-lg overflow-hidden mb-4 border-radius-2xl card-custom">
            
            <div class="header-tamep d-flex flex-column flex-sm-row align-items-center px-3 px-md-4 py-3 text-white text-center text-sm-start gap-2 gap-sm-3 border-bottom">
              <div class="logo-container-left shadow-sm mt-sm-0 mt-2">
                <img :src="logo" class="logo-img">
              </div>
              <div class="border-start ps-3 border-white opacity-8 text-start w-100">
                <h6 class="mb-0 text-white text-uppercase font-weight-bolder">Registro Maestro de Activo</h6>
                <small class="opacity-7 text-xs d-block mt-1 mt-sm-0">Configuración completa de inventario predictivo</small>
              </div>
            </div>

            <div class="card-body p-3 p-md-4 bg-section-custom">
              
              <div class="section-title mb-3 d-flex align-items-center">
                <div class="step-number me-2">1</div>
                <h6 class="font-weight-bolder mb-0 text-azul-tamep text-uppercase text-sm">Identificación y Clasificación</h6>
              </div>
              
              <div class="row g-3 mb-4">
                <div class="col-6 col-md-3">
                  <label class="label-custom">Código</label>
                  <argon-input v-model="formularioActivo.codigo_tamep" />
                </div>
                <div class="col-6 col-md-3">
                  <label class="label-custom">Histórico</label>
                  <argon-input v-model="formularioActivo.codigo_tab" />
                </div>
                <div class="col-12 col-md-6">
                  <label class="label-custom">Nombre Específico (Marca/Modelo)</label>
                  <argon-input v-model="formularioActivo.nombre_especifico" required />
                </div>
                
                <div class="col-12 col-md-4">
                  <label class="label-custom">Nro. de Serie (Opcional)</label>
                  <argon-input v-model="formularioActivo.serie" />
                </div>
                <div class="col-12 col-md-4 position-relative">
                  <label class="label-custom">Tipo de Activo</label>
                  <input type="text" class="form-control form-select-custom" 
                         v-model="busquedaTipo" 
                         @focus="inputFoco = true" 
                         @blur="ocultarFoco" 
                         placeholder="Escriba para buscar... (Ej: Laptop)" 
                         autocomplete="off" required />
                  <ul v-if="inputFoco && tiposSugeridos.length > 0" class="list-group position-absolute w-100 mt-1 shadow-lg bg-white" style="z-index: 1000; max-height: 250px; overflow-y: auto;">
                    <li v-for="t in tiposSugeridos" :key="t.id" class="list-group-item list-group-item-action py-2 suggestion-item" @click="seleccionarTipo(t)" style="cursor:pointer">
                      <span class="d-block font-weight-bold text-sm text-dark">{{ t.nombre }}</span>
                      <span class="d-block text-xxs text-secondary">Grupo: {{ t.grupo_nombre || 'Asociado' }}</span>
                    </li>
                  </ul>
                </div>
                <div class="col-12 col-md-4">
                  <label class="label-custom">Grupo Contable (Auto)</label>
                  <select class="form-select-custom border-secondary text-secondary" v-model="formularioActivo.grupo_contable_seleccionado" disabled>
                    <option v-for="g in gruposContables" :key="g.id" :value="g.id">{{ g.nombre }}</option>
                  </select>
                </div>
              </div>

              <div class="section-title mb-3 d-flex align-items-center mt-4 mt-md-5">
                <div class="step-number me-2" :class="esGrupoIA ? 'bg-warning text-dark' : 'bg-secondary'">
                  <i class="fas fa-robot"></i>
                </div>
                <h6 class="font-weight-bolder mb-0 text-azul-tamep text-uppercase text-sm">Parámetros de Operación e IA</h6>
              </div>
              
              <div class="row g-3 mb-4 p-3 border-radius-lg border-start border-4" 
                   :class="esGrupoIA ? 'bg-ia border-warning' : 'bg-gray-100 border-secondary opacity-7'">
                
                <div class="col-6 col-md-3">
                  <label class="label-custom">Perfil de Uso</label>
                  <select class="form-select-custom px-2" v-model="formularioActivo.perfil_uso" :disabled="!esGrupoIA">
                    <option value="N/A" v-if="!esGrupoIA">Sin IA</option>
                    <template v-else>
                      <option value="Oficina">Oficina (8h)</option>
                      <option value="Intensivo">Intensivo (12h)</option>
                      <option value="Critico">Crítico (24h)</option>
                    </template>
                  </select>
                </div>
                <div class="col-6 col-md-3">
                  <label class="label-custom">Estado Físico</label>
                  <select class="form-select-custom px-2" v-model="formularioActivo.estado_actual">
                    <option value="Bueno">Bueno</option>
                    <option value="Regular">Regular</option>
                    <option value="Malo">Malo</option>
                  </select>
                </div>
                <div class="col-6 col-md-3">
                  <label class="label-custom">Situación</label>
                  <select class="form-select-custom px-2" v-model="formularioActivo.situacion_activo">
                    <option value="Disponible">Disponible (En Almacén)</option>
                    <option value="Asignado">Asignar de Inmediato</option>
                  </select>
                </div>
                <div class="col-6 col-md-3">
                  <label class="label-custom text-truncate">Uso Inicial (Hrs)</label>
                  <argon-input type="number" v-model="formularioActivo.uso_inicial_estimado" :disabled="!esGrupoIA" />
                </div>
              </div>

              <div v-if="esGrupoIA" class="animate__animated animate__fadeIn">
                <div class="section-title mb-3 d-flex align-items-center mt-4 mt-md-5">
                  <div class="step-number me-2">3</div>
                  <h6 class="font-weight-bolder mb-0 text-azul-tamep text-uppercase text-sm">Especificaciones Técnicas</h6>
                </div>
                <div class="row g-3 mb-4">
                  <div class="col-12 col-md-4"><label class="label-custom">CPU / Procesador</label><argon-input v-model="techSpecs.cpu" /></div>
                  <div class="col-12 col-md-4"><label class="label-custom">Memoria RAM</label><argon-input v-model="techSpecs.ram" /></div>
                  <div class="col-12 col-md-4"><label class="label-custom">MAC ADDR / Red</label><argon-input v-model="techSpecs.mac" /></div>
                </div>
              </div>

              <div class="row g-3 mb-4 mt-2">
                <div class="col-12">
                  <label class="label-custom">Descripción / Observaciones Generales</label>
                  <textarea class="form-control" v-model="formularioActivo.descripcion" rows="2"></textarea>
                </div>
              </div>

              <div class="section-title mb-3 d-flex align-items-center mt-4 mt-md-5">
                <div class="step-number me-2">
                  <i class="fas fa-camera"></i>
                </div>
                <h6 class="font-weight-bolder mb-0 text-azul-tamep text-uppercase text-sm">Evidencia Fotográfica</h6>
              </div>
              <div class="row g-3 mb-4 mb-md-5">
                <div class="col-12 col-md-4">
                  <label class="label-custom text-center">Imagen Principal</label>
                  <div class="upload-box" :class="{'has-image': previsualizaciones.imagen_activo}">
                    <img v-if="previsualizaciones.imagen_activo" :src="previsualizaciones.imagen_activo" class="img-preview" />
                    <div v-else class="upload-placeholder"><i class="fas fa-image fa-2x mb-2"></i><br><small>FRONTAL</small></div>
                    <input type="file" class="file-hidden" @change="handleFileUpload($event, 'imagen_activo')" accept="image/*" />
                    <button v-if="previsualizaciones.imagen_activo" type="button" @click="eliminarImagen('imagen_activo')" class="btn-remove-img"><i class="fas fa-times"></i></button>
                  </div>
                </div>
                <div class="col-12 col-md-4">
                  <label class="label-custom text-center">Placa Técnica / Lateral</label>
                  <div class="upload-box" :class="{'has-image': previsualizaciones.imagen_lateral}">
                    <img v-if="previsualizaciones.imagen_lateral" :src="previsualizaciones.imagen_lateral" class="img-preview" />
                    <div v-else class="upload-placeholder"><i class="fas fa-barcode fa-2x mb-2"></i><br><small>CARACTERÍSTICAS</small></div>
                    <input type="file" class="file-hidden" @change="handleFileUpload($event, 'imagen_lateral')" accept="image/*" />
                    <button v-if="previsualizaciones.imagen_lateral" type="button" @click="eliminarImagen('imagen_lateral')" class="btn-remove-img"><i class="fas fa-times"></i></button>
                  </div>
                </div>
                <div class="col-12 col-md-4">
                  <label class="label-custom text-center">Serial Number (S/N)</label>
                  <div class="upload-box" :class="{'has-image': previsualizaciones.imagen_sn}">
                    <img v-if="previsualizaciones.imagen_sn" :src="previsualizaciones.imagen_sn" class="img-preview" />
                    <div v-else class="upload-placeholder"><i class="fas fa-search-plus fa-2x mb-2"></i><br><small>ZOOM SERIAL</small></div>
                    <input type="file" class="file-hidden" @change="handleFileUpload($event, 'imagen_sn')" accept="image/*" />
                    <button v-if="previsualizaciones.imagen_sn" type="button" @click="eliminarImagen('imagen_sn')" class="btn-remove-img"><i class="fas fa-times"></i></button>
                  </div>
                </div>
              </div>

              <div class="section-title mb-3 d-flex align-items-center mt-4 mt-md-5">
                <div class="step-number me-2"><i class="fas fa-building"></i></div>
                <h6 class="font-weight-bolder mb-0 text-azul-tamep text-uppercase text-sm">Finanzas y Estructura Organizacional de Origen</h6>
              </div>
              <div class="row g-3">
                <div class="col-12 col-md-6">
                  <label class="label-custom">Fecha de Adquisición</label>
                  <argon-input type="date" v-model="formularioActivo.fecha_adquisicion" />
                </div>
                <div class="col-12 col-md-6">
                  <label class="label-custom">Costo (Bs.)</label>
                  <argon-input type="number" step="0.01" v-model="formularioActivo.costo" />
                </div>
                <div class="col-12 col-md-4">
                  <label class="label-custom">Regional</label>
                  <select class="form-select-custom" v-model="formularioActivo.regional">
                    <option value="">Seleccionar Regional</option>
                    <option v-for="r in regionalesList" :key="r.id" :value="r.id">{{ r.nombre }}</option>
                  </select>
                </div>
                <div class="col-12 col-md-4">
                  <label class="label-custom">Gerencia de Origen</label>
                  <select class="form-select-custom" v-model="formularioActivo.gerencia" :disabled="!formularioActivo.regional">
                    <option value="">Seleccionar Gerencia</option>
                    <option v-for="g in gerenciasList" :key="g.id" :value="g.id">{{ g.nombre }}</option>
                  </select>
                </div>
                <div class="col-12 col-md-4">
                  <label class="label-custom">Sección / Área</label>
                  <select class="form-select-custom" v-model="formularioActivo.seccion" :disabled="!formularioActivo.gerencia">
                    <option value="">Seleccionar Sección</option>
                    <option v-for="s in seccionesList" :key="s.id" :value="s.id">{{ s.nombre }}</option>
                  </select>
                </div>
              </div>

              <div class="mt-4 mt-md-5 text-end border-top pt-4">
                <button type="submit" class="btn-tamep-primary shadow-lg w-100 py-3 text-wrap" :disabled="cargando">
                  <i class="fas" :class="formularioActivo.situacion_activo === 'Disponible' ? 'fa-save' : 'fa-arrow-right'"></i>
                  <span class="ms-2">
                    {{ cargando ? 'PROCESANDO...' : (formularioActivo.situacion_activo === 'Disponible' ? 'FINALIZAR Y GENERAR QR' : 'CONTINUAR A ASIGNACIÓN')}}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-4 d-print-block">
          <PreviewEtiquetaActivo 
            :logo="logo"
            :qrUrl="qrUrl"
            :codigoTamep="qrUrl ? (datosEtiqueta.codigo_tamep || 'N/A') : (formularioActivo.codigo_tamep || 'N/A')"
            :codigoTab="qrUrl ? (datosEtiqueta.codigo_tab || '----') : (formularioActivo.codigo_tab || '----')"
            :procesoFinalizadoExitosa="procesoFinalizadoExitosa"
            @imprimir="imprimirEtiquetaPDF"
            @registrar-otro="prepararNuevoRegistro"
          />
        </div>
      </div>
    </form>

    <div v-if="mostrarModalExtra" class="tamep-modal-overlay">
      <div class="tamep-modal-content p-3 p-md-4 animate__animated animate__fadeInDown">
        <div class="modal-header-tamep mb-4 text-white text-center p-3 rounded-3 shadow-sm">
          <h6 class="mb-0 text-white font-weight-bold text-sm"><i class="fas fa-user-check me-2"></i>VINCULAR CUSTODIO LEGAL</h6>
        </div>
        
        <div class="row g-3">
          <SelectorJerarquicoPersonal
            v-model:regional="formularioExtra.regional"
            v-model:gerencia="formularioExtra.gerencia"
            v-model:seccion="formularioExtra.seccion"
            v-model:cargo="formularioExtra.cargo"
            v-model:usuario="formularioExtra.usuario"
            colClass="col-12 col-md-6"
            colClassUsuario="col-12"
            selectClass="form-select-custom mb-0"
          />

          <div class="col-12 col-md-6">
            <label class="label-custom">Nro. de Acta de Designación</label>
            <input type="text" v-model="formularioExtra.num_acta" class="form-select-custom mb-0" placeholder="Ej: ACTA-001/2026">
          </div>

          <div class="col-12 col-md-6">
            <label class="label-custom">Tipo de Asignación</label>
            <select v-model="formularioExtra.tipo" class="form-select-custom mb-0">
              <option value="Permanente">Permanente (Cargo)</option>
              <option value="Temporal">Préstamo Temporal</option>
            </select>
          </div>

          <div v-if="formularioExtra.tipo === 'Temporal'" class="col-12 col-md-6">
            <label class="label-custom">Fecha de Devolución Estimada</label>
            <input type="date" v-model="formularioExtra.fecha_fin_estimada" class="form-select-custom mb-0">
          </div>

          <div class="col-12">
            <label class="label-custom">Observaciones de Entrega</label>
            <textarea v-model="formularioExtra.observaciones" class="form-select-custom mb-2" rows="2" placeholder="Notas adicionales..."></textarea>
          </div>
        </div>
        
        <div class="d-flex gap-2 mt-3">
          <button type="button" @click="mostrarModalExtra = false" class="btn btn-outline-secondary w-50 py-3 mb-0">Cancelar</button>
          <button type="button" @click="finalizarProcesoCompleto" class="btn-tamep-primary w-50 py-3 mb-0">CONFIRMAR VÍNCULO</button>
        </div>
      </div>
    </div>
  </div>
</template>
